import { FormEvent, useEffect, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";

import { Link } from "react-router-dom";
import ProviderNavigation from "./ProviderNavigation";
import { getPortalLandingPath } from "./util/portalRoutes";
import { safeInternalPath } from "./util/safeInternalPath";
import { useAuth0 } from "@auth0/auth0-react";

export const GET_USER_AUTH_INFO = gql`
  query GetUserAuthInfo($username: String!) {
    userAuthInfo(username: $username) {
      authProvider
      role
    }
  }
`;

export default function LoginPage() {
  const { loginWithRedirect } = useAuth0();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [step, setStep] = useState<"email_entry" | "password_entry">(
    "email_entry",
  );

  const [getUserAuthInfo, { loading: credsLoading }] = useLazyQuery(
    GET_USER_AUTH_INFO,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Clear stale session state so useAuth falls back to roles[0] after login.
  useEffect(() => {
    sessionStorage.removeItem("authRedirect");
    localStorage.removeItem("activeRole");
  }, []);

  const query = new URLSearchParams(window.location.search);
  const loginType = query.get("type");
  const redirectTo = query.get("redirect_to");

  const loginWithAuth0 = (role: string) => {
    loginWithRedirect({
      appState: {
        returnTo: safeInternalPath(redirectTo) ?? getPortalLandingPath(role),
      },
      authorizationParams: {
        login_hint: email,
      },
    });
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");

    const { data, error } = await getUserAuthInfo({
      variables: { username: email },
    });
    if (error) {
      setMessage(
        error.networkError
          ? "Failed to connect. Please check your internet connection and try again."
          : "Failed to determine login method. Please try again.",
      );
      return;
    }
    if (data?.userAuthInfo?.authProvider === "AUTH0") {
      loginWithAuth0(data.userAuthInfo.role);
    } else if (data?.userAuthInfo?.authProvider === "SPREE") {
      setStep("password_entry");
    }
  };

  const authenticate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    setIsLocked(false);
    setIsWarning(false);
    setAuthLoading(true);

    const startLoadingTime = Date.now();
    const minLoadingDuration = 500;

    fetch("/graphql/login", {
      method: "POST",
      body: JSON.stringify({
        username: email,
        password: password,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        console.log(res);
        if (res.redirected) {
          // Return to the originally requested page (e.g. /analytics) when present, else the backend's default landing path.
          window.location.href = safeInternalPath(redirectTo) ?? res.url;
          return; // if being redirected, the result is not json
        }
        return res.json();
      })
      .then((res) => {
        // ensure loading state shows for at least minLoadingDuration
        const elapsedTime = Date.now() - startLoadingTime;
        const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);

        setTimeout(() => {
          if (!res) return;

          if (res.locked) {
            setIsLocked(true);
            setMessage(res.data);
          } else if (res.warning) {
            setIsWarning(true);
            setMessage(res.data);
          } else {
            setMessage(res.data);
          }

          setAuthLoading(false);
        }, remainingTime);
      })
      .catch(() => {
        setMessage("Login failed. Please try again.");
        setAuthLoading(false);
      });
  };

  const getButtonText = () => {
    if (authLoading) {
      return "Logging in...";
    }
    if (credsLoading) {
      return "Checking...";
    }
    return step === "email_entry" ? "Continue" : "Log in";
  };

  return (
    <>
      <ProviderNavigation />
      <div className="row" id="login_row">
        <div className="tw-mx-auto tw-w-1/3 1040:tw-w-[90%]">
          <div className="panel panel-default tw-mt-[80px]">
            <div className="panel-heading">
              <h3 className="panel-title">
                {loginType == "provider"
                  ? "Log in as Provider"
                  : "Log in as Existing User"}
              </h3>
            </div>
            <div id="existing-customer" className="panel-body">
              <form
                onSubmit={
                  step === "email_entry" ? handleEmailSubmit : authenticate
                }
              >
                <fieldset
                  id="password-credentials"
                  disabled={authLoading || credsLoading}
                >
                  <div className="tw-mb-[15px]">
                    <input
                      id="login-email"
                      className="form-control"
                      type="text"
                      placeholder="Email"
                      disabled={step === "password_entry"}
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                    />
                  </div>
                  {step === "password_entry" && (
                    <div className="tw-mb-[15px]">
                      <input
                        id="login-password"
                        className="form-control"
                        type="password"
                        placeholder="Password"
                        onChange={(e) => setPassword(e.target.value)}
                        value={password}
                      />
                    </div>
                  )}
                  {redirectTo ? (
                    <input
                      type="hidden"
                      name="redirect_to"
                      value={redirectTo}
                    />
                  ) : null}
                  {loginType ? (
                    <input type="hidden" name="type" value={loginType} />
                  ) : null}

                  {/* Lockout Message */}
                  {isLocked && (
                    <div className="tw-mb-[15px] tw-rounded-[3px] tw-border tw-border-red-200 tw-bg-red-50 tw-p-[15px]">
                      <h4 className="tw-mb-[10px] tw-font-bold tw-text-red-700">
                        Too many login attempts
                      </h4>
                      <p className="tw-mb-[10px] tw-text-red-600">{message}</p>
                    </div>
                  )}

                  {/* Warning Message */}
                  {isWarning && (
                    <div className="tw-mb-[15px] tw-rounded-[3px] tw-border tw-border-yellow-200 tw-bg-yellow-50 tw-p-[15px]">
                      <h4 className="tw-mb-[10px] tw-font-bold tw-text-yellow-700">
                        Invalid password
                      </h4>
                      <p className="tw-mb-[10px] tw-text-yellow-600">
                        {message}
                      </p>
                      <p className="tw-text-yellow-600">
                        Forgot your password?{" "}
                        <Link to="/password/request-password-reset">
                          Reset it now
                        </Link>
                      </p>
                    </div>
                  )}

                  {/* Generic Error Message */}
                  {message && !isLocked && !isWarning && (
                    <div className="tw-mb-[10px] tw-text-red-1">{message}</div>
                  )}

                  <div className="tw-mb-[15px]">
                    <button
                      id="login-submit"
                      className="btn btn-lg btn-success btn-block"
                      type="submit"
                      data-testid="login-submit-button"
                      disabled={authLoading || credsLoading}
                    >
                      {getButtonText()}
                    </button>
                  </div>
                </fieldset>
              </form>
              <div className="tw-text-center">
                <Link to="/password/request-password-reset">
                  Forgot Password?
                </Link>
              </div>
            </div>
          </div>
          <div className="password-login-link">
            <Link to="/login?type=provider">
              Log in with just an email instead
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
